import { AccountRow } from "./AccountRow";
import { AccountStatuses, BreadAccount } from "breadcommon";
import { useContext, useState } from "react";
import { AccountsContext } from "../firebaseio/AccountsContext";
import { LoadingInitialDataContext } from "../LoadUser";
import { RyeSkeletonList } from "../rye/RyeSkeleton";
import { RyeDraggableList } from "../rye/RyeDraggableList";
import { buildClasses } from "../utils/buildClasses";
import { firestoreUpdateAccountsOrder } from "../firebaseio/firestoreIo";
import { UserContext } from "../firebaseio/UserContext";
import { RyeIcon } from "../rye/RyeIcon";
import { HorizontalSpacer } from "../utils/Utils";
import React from "react";

export function AccountsList(): JSX.Element {
  const accounts = useContext(AccountsContext);
  const loadingInitialData = useContext(LoadingInitialDataContext);
  const user = useContext(UserContext);
  const [showClosedAccounts, setShowClosedAccounts] = useState(false);

  if (
    loadingInitialData.accounts ||
    loadingInitialData.institutions ||
    loadingInitialData.transactions
  ) {
    return <RyeSkeletonList rowClassName="h-14" />;
  }

  let accountRows: { id: string; value: JSX.Element }[] = [];
  Array.from(accounts.values())
    .sort((a, b) => a.order_position - b.order_position)
    .forEach((account: BreadAccount) => {
      if (account.status === AccountStatuses.ACTIVE) {
        accountRows.push({
          id: account.id,
          value: <AccountRow account={account}></AccountRow>,
        });
      }
    });

  const closedAccounts = Array.from(accounts.values())
    .filter((account) => account.status === AccountStatuses.CLOSED)
    .sort((a, b) => a.name.localeCompare(b.name));

  const activeList =
    accountRows.length === 0 ? (
      <div
        className={buildClasses(
          "w-full",
          "flex",
          "flex-col",
          "items-center",
          "p-10",
        )}
      >
        <div className={buildClasses("text-on-surface-500", "mt-12")}>
          Nothing to show here yet! Add an account to get started.
        </div>
      </div>
    ) : (
      <RyeDraggableList
        inputItems={accountRows}
        onOrderUpdate={function (newIdList: string[]): void {
          firestoreUpdateAccountsOrder(user.uid, newIdList);
        }}
        variant={"dividing-lines"}
      />
    );

  return (
    <div className={buildClasses("w-full", "flex", "flex-col")}>
      {activeList}
      {closedAccounts.length > 0 && (
        <div
          className={buildClasses(
            "mt-8",
            "border-t",
            "border-on-surface-200",
            "pt-6",
            "pb-4",
          )}
        >
          <button
            onClick={() => setShowClosedAccounts(!showClosedAccounts)}
            className={buildClasses(
              "flex",
              "items-center",
              "gap-2",
              "text-md",
              "font-medium",
              "text-on-surface-500",
              "hover:text-on-surface-800",
              "transition-colors",
              "focus:outline-none",
            )}
          >
            <RyeIcon
              name={
                showClosedAccounts
                  ? "keyboard_arrow_down"
                  : "keyboard_arrow_right"
              }
              size="sm"
            />
            Closed Accounts
          </button>
          {showClosedAccounts && (
            <div className={buildClasses("mt-4", "flex", "flex-col")}>
              {closedAccounts.map((account, index) => (
                <React.Fragment key={account.id}>
                  {index > 0 && (
                    <div
                      className={buildClasses(
                        "h-[10px]",
                        "flex",
                        "items-center",
                      )}
                    >
                      <div
                        className={buildClasses(
                          "w-full",
                          "h-px",
                          "bg-surface-500",
                        )}
                      />
                    </div>
                  )}
                  <div
                    className={buildClasses(
                      "flex",
                      "items-center",
                      "px-5",
                      "rounded-lg",
                      "bg-surface",
                      "border-none",
                    )}
                  >
                    <div
                      className={buildClasses("w-[1.33rem]", "flex-shrink-0")}
                    />
                    <HorizontalSpacer width={20} />
                    <AccountRow account={account} />
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
